import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Utensils, LogOut, Plus, Pencil, X, BarChart3, Users, Star, Tag, Gift, Search, Download, Trash2, ImagePlus, Video, ChevronRight, ChevronDown, Leaf, Drumstick, Award, Flame as Spicy, Settings as SettingsIcon, Sun, Moon, Flame, QrCode, Printer, Sparkles, Check, Lightbulb, ChefHat, Wand2, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import Logo from "../components/Logo";
import { dishImg, onDishImgError } from "../lib/dishImage";
import CuisineTagInput from "../components/CuisineTagInput";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "menu", label: "Menu Editor", icon: Utensils },
  { key: "categories", label: "Categories", icon: Tag },
  { key: "margin", label: "Menu Engineering", icon: BarChart3 },
  { key: "ratings", label: "Quality Control", icon: Star },
  { key: "crm", label: "Customer CRM", icon: Users },
  { key: "loyalty", label: "Loyalty", icon: Gift },
  { key: "qr", label: "Tables & QR", icon: QrCode },
  { key: "wishlist", label: "Guest Wishlist", icon: Lightbulb },
  { key: "ai-usage", label: "AI Usage", icon: Sparkles },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

export default function OwnerDashboard() {
  const { user, logout, refresh } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const [tab, setTab] = useState("dashboard");
  const [activeRid, setActiveRid] = useState(() => localStorage.getItem("crave_active_rid") || "");
  const [resto, setResto] = useState(null);
  const [cats, setCats] = useState([]);
  const [menu, setMenu] = useState([]);
  const [margin, setMargin] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [restoPicker, setRestoPicker] = useState(false);
  const [billing, setBilling] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  // Enrich user on mount so we get restaurants list
  useEffect(() => { refresh(); }, []); // eslint-disable-line

  // Sync active restaurant from user data
  useEffect(() => {
    if (user?.restaurants?.length) {
      if (!activeRid || !user.restaurants.find((r) => r.id === activeRid)) {
        const rid = user.restaurant_id || user.restaurants[0].id;
        setActiveRid(rid);
        localStorage.setItem("crave_active_rid", rid);
      }
    } else if (user?.restaurant_id && !activeRid) {
      setActiveRid(user.restaurant_id);
      localStorage.setItem("crave_active_rid", user.restaurant_id);
    }
  }, [user, activeRid]);

  const load = async () => {
    try {
      const [r, c, m, an, rg, cus, ly, db, bl] = await Promise.all([
        api.get("/owner/restaurant"),
        api.get("/owner/categories"),
        api.get("/owner/menu"),
        api.get("/owner/analytics/margin"),
        api.get("/owner/analytics/ratings"),
        api.get("/owner/customers"),
        api.get("/owner/loyalty"),
        api.get("/owner/dashboard"),
        api.get("/owner/billing"),
      ]);
      setResto(r.data); setCats(c.data); setMenu(m.data); setMargin(an.data.items || []);
      setRatings(rg.data); setCustomers(cus.data); setLoyalty(ly.data); setDashboard(db.data);
      setBilling(bl.data);
    } catch { /* noop */ }
  };
  useEffect(() => { if (activeRid) load(); }, [activeRid]);

  const switchRestaurant = (rid) => {
    setActiveRid(rid);
    localStorage.setItem("crave_active_rid", rid);
    setRestoPicker(false);
    toast.success("Switched restaurant");
  };

  const ratingsById = useMemo(() => Object.fromEntries(ratings.map((r) => [r.id, r])), [ratings]);
  const restaurants = user?.restaurants || [];

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)", color: "var(--fg)" }}>
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r hidden md:flex flex-col" style={{ borderColor: "var(--border)", background: "var(--bg-muted)" }}>
        <div className="p-5 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-2">
            <Logo size={32} />
            <div className="font-display text-xl">Mnu<span className="gold-text">Verse</span></div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] mt-3" style={{ color: "var(--fg-muted)" }}>Operator</div>
          <div className="text-sm mt-0.5">{user?.name}</div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => {
            const Ico = n.icon;
            const active = tab === n.key;
            return (
              <button key={n.key} onClick={() => setTab(n.key)} data-testid={`sb-${n.key}`}
                style={{ background: active ? "var(--accent)" : "transparent", color: active ? "var(--accent-fg)" : "var(--fg)" }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm hover:bg-white/5 transition-colors">
                <Ico className="w-4 h-4" /> {n.label}
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t space-y-1" style={{ borderColor: "var(--border)" }}>
          <Link to="/staff" className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm hover:bg-white/5" data-testid="to-staff">
            <Utensils className="w-4 h-4" /> Staff / KDS
          </Link>
          {resto && <Link to={`/r/${resto.slug}`} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm hover:bg-white/5" data-testid="view-menu-link">
            <ChevronRight className="w-4 h-4" /> Live menu
          </Link>}
          <button onClick={toggleTheme} data-testid="owner-theme-toggle" className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm hover:bg-white/5">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />} {theme === "dark" ? "Light" : "Dark"} mode
          </button>
          <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm hover:bg-white/5 text-red-400" data-testid="owner-logout">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="border-b px-6 py-4 flex items-center justify-between" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
          <div className="flex items-center gap-3">
            {resto?.logo_url && <img src={resto.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />}
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: "var(--fg-muted)" }}>{NAV.find((n) => n.key === tab)?.label}</div>
              <div className="flex items-center gap-2">
                <div className="font-display text-2xl">{resto?.name || "—"}</div>
                {restaurants.length > 1 && (
                  <div className="relative">
                    <button onClick={() => setRestoPicker((v) => !v)} data-testid="resto-picker-btn" className="flex items-center gap-1 text-xs px-2 py-1 rounded-full hover:bg-white/5" style={{ border: "1px solid var(--border)" }}>
                      Switch <ChevronDown className="w-3 h-3" />
                    </button>
                    {restoPicker && (
                      <div className="absolute top-full mt-1 left-0 z-30 rounded-xl shadow-2xl min-w-[240px]" style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }} data-testid="resto-picker-menu">
                        {restaurants.map((r) => (
                          <button key={r.id} onClick={() => switchRestaurant(r.id)} data-testid={`picker-${r.id}`} className="w-full text-left px-3 py-2 hover:bg-white/5 first:rounded-t-xl last:rounded-b-xl text-sm flex items-center justify-between">
                            <span>{r.name}</span>
                            {activeRid === r.id && <span className="text-[10px]" style={{ color: "var(--accent)" }}>● active</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {resto?.tagline && <div className="text-xs italic mt-0.5" style={{ color: "var(--fg-muted)" }}>{resto.tagline}</div>}
            </div>
          </div>

          {/* Mobile nav */}
          <div className="md:hidden flex items-center gap-2">
            <select value={tab} onChange={(e) => setTab(e.target.value)} className="bg-transparent border rounded-lg px-2 py-1 text-xs" style={{ borderColor: "var(--border)" }}>
              {NAV.map((n) => <option key={n.key} value={n.key}>{n.label}</option>)}
            </select>
            <button onClick={toggleTheme} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ border: "1px solid var(--border)" }}>
              {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto px-6 py-6">
          {billing && <TrialBanner billing={billing} onUpgrade={() => setShowUpgrade(true)} />}
          {tab === "dashboard" && dashboard && <DashboardTab d={dashboard} />}
          {tab === "menu" && (
            <MenuTab menu={menu} ratingsById={ratingsById} categories={cats} onEdit={setEditing} onCreate={() => setCreating(true)} />
          )}
          {tab === "categories" && <CategoriesTab cats={cats} onReload={load} />}
          {tab === "margin" && <MarginTab margin={margin} />}
          {tab === "ratings" && <RatingsTab ratings={ratings} />}
          {tab === "crm" && <CRMTab customers={customers} onOpenDetail={setCustomerDetail} />}
          {tab === "loyalty" && loyalty && <LoyaltyTab loyalty={loyalty} menu={menu} onSaved={load} />}
          {tab === "qr" && resto && <QRTab resto={resto} />}
          {tab === "wishlist" && <WishlistTab />}
          {tab === "ai-usage" && resto && <AIUsageTab rid={resto.id} />}
          {tab === "settings" && resto && <SettingsTab resto={resto} onSaved={() => { load(); refresh(); }} />}
        </main>
      </div>

      {(editing || creating) && (
        <ItemEditor
          item={editing} categories={cats}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSave={async (payload, id) => {
            try {
              if (id) await api.put(`/owner/menu/${id}`, payload);
              else await api.post("/owner/menu", payload);
              toast.success("Saved");
              setEditing(null); setCreating(false);
              load();
            } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
          }}
          onDelete={async (id) => {
            try { await api.delete(`/owner/menu/${id}`); toast.success("Deleted"); setEditing(null); load(); } catch { toast.error("Delete failed"); }
          }}
        />
      )}
      {customerDetail && <CustomerDetailModal customerId={customerDetail} onClose={() => setCustomerDetail(null)} />}
      {showUpgrade && billing && (
        <UpgradeModal
          billing={billing}
          onClose={() => setShowUpgrade(false)}
          onUpgraded={async () => { setShowUpgrade(false); await load(); }}
        />
      )}
    </div>
  );
}

/* ---------- Dashboard tab ---------- */
const COLORS = ["#E5A93C", "#3B82F6", "#10B981", "#F97316", "#EF4444", "#8B5CF6"];
function DashboardTab({ d }) {
  const statusData = Object.entries(d.status_counts || {}).map(([k, v]) => ({ name: k, value: v }));
  return (
    <div className="space-y-6" data-testid="dashboard-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Revenue today" value={`₹${d.revenue_today?.toFixed?.(0) || 0}`} />
        <KPI label="Orders today" value={d.orders_today} />
        <KPI label="Total revenue" value={`₹${d.revenue_total?.toFixed?.(0) || 0}`} />
        <KPI label="Active tables" value={(d.active_tables || []).length} />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 p-5 rounded-2xl" style={{ border: "1px solid var(--border)", background: "var(--bg-card)" }}>
          <h3 className="font-display text-xl">Orders by hour (last 24h)</h3>
          <div className="mt-3 h-64" data-testid="chart-hours">
            <ResponsiveContainer>
              <BarChart data={d.hours_series}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="hour" stroke="currentColor" style={{ fontSize: 11 }} />
                <YAxis stroke="currentColor" style={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#121418", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8 }} />
                <Bar dataKey="orders" fill="#E5A93C" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="p-5 rounded-2xl" style={{ border: "1px solid var(--border)", background: "var(--bg-card)" }}>
          <h3 className="font-display text-xl">Status mix</h3>
          <div className="mt-3 h-56" data-testid="chart-status">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={3}>
                  {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#121418", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="p-5 rounded-2xl" style={{ border: "1px solid var(--border)", background: "var(--bg-card)" }}>
          <h3 className="font-display text-xl">Top dishes today</h3>
          <div className="mt-3 space-y-2" data-testid="top-dishes">
            {d.top_dishes?.length ? d.top_dishes.map((t, i) => (
              <div key={t.id} className="flex items-center gap-3">
                <span className="text-xs font-mono w-6" style={{ color: "var(--fg-muted)" }}>#{i + 1}</span>
                <span className="flex-1">{t.name}</span>
                <span className="font-mono text-sm" style={{ color: "var(--accent)" }}>{t.count} plates</span>
              </div>
            )) : <div className="text-sm" style={{ color: "var(--fg-muted)" }}>No orders yet today.</div>}
          </div>
        </div>

        <div className="p-5 rounded-2xl" style={{ border: "1px solid var(--border)", background: "var(--bg-card)" }}>
          <h3 className="font-display text-xl">Active tables</h3>
          <div className="mt-3 space-y-2" data-testid="active-tables">
            {(d.active_tables || []).length === 0 && <div className="text-sm" style={{ color: "var(--fg-muted)" }}>All tables turned. Quiet service.</div>}
            {(d.active_tables || []).map((t) => (
              <div key={t.table} className="flex items-center justify-between p-2 rounded-lg" style={{ background: "var(--glass-light)" }}>
                <div>
                  <div className="font-display text-base">Table {t.table}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>{t.status} · {t.orders} orders</div>
                </div>
                <span className="font-mono" style={{ color: "var(--accent)" }}>₹{t.total?.toFixed?.(0)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="p-5 rounded-2xl" style={{ border: "1px solid var(--border)", background: "var(--bg-card)" }}>
        <h3 className="font-display text-xl">Recent orders</h3>
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="recent-orders-table">
            <thead className="text-[11px] font-mono uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>
              <tr style={{ borderBottom: "1px solid var(--border)" }}>
                <th className="text-left py-2 px-3">Time</th>
                <th className="text-left py-2 px-3">Guest</th>
                <th className="text-left py-2 px-3">Table</th>
                <th className="text-right py-2 px-3">Total</th>
                <th className="text-right py-2 px-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {(d.recent_orders || []).map((o) => (
                <tr key={o.id} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td className="py-2 px-3 font-mono text-xs">{new Date(o.created_at).toLocaleTimeString()}</td>
                  <td className="py-2 px-3">{o.customer_name || "Guest"}</td>
                  <td className="py-2 px-3 font-mono">{o.table_no}</td>
                  <td className="py-2 px-3 text-right font-mono" style={{ color: "var(--accent)" }}>₹{o.total?.toFixed?.(0)}</td>
                  <td className="py-2 px-3 text-right"><span className="text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest" style={{ background: "var(--glass-light)" }}>{o.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------- Menu tab ---------- */
function hasItemImage(m) {
  if ((m?.image_url || "").trim()) return true;
  return (m?.images || []).some((u) => typeof u === "string" && u.trim().length > 0);
}

function MenuTab({ menu, ratingsById, onEdit, onCreate, categories }) {
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState(""); // "" means All
  const [quick, setQuick] = useState(""); // "veg" | "non-veg" | "sold-out" | "featured" | ""
  const missingCount = menu.filter((m) => !hasItemImage(m)).length;

  const visible = useMemo(() => {
    let out = menu;
    if (onlyMissing) out = out.filter((m) => !hasItemImage(m));
    if (activeCat) out = out.filter((m) => m.category_id === activeCat);
    if (quick === "veg") out = out.filter((m) => m.is_veg === true);
    else if (quick === "non-veg") out = out.filter((m) => m.is_veg === false);
    else if (quick === "sold-out") out = out.filter((m) => m.is_sold_out);
    else if (quick === "featured") out = out.filter((m) => m.is_featured);
    const q = query.trim().toLowerCase();
    if (q) {
      out = out.filter((m) =>
        m.name.toLowerCase().includes(q)
        || (m.description || "").toLowerCase().includes(q)
        || (m.ingredients || []).some((x) => String(x).toLowerCase().includes(q))
        || (m.tags || []).some((x) => String(x).toLowerCase().includes(q))
      );
    }
    return out;
  }, [menu, onlyMissing, activeCat, quick, query]);

  const clearFilters = () => { setOnlyMissing(false); setActiveCat(""); setQuick(""); setQuery(""); };
  const hasFilter = onlyMissing || activeCat || quick || query;
  const cats = categories || [];
  const countFor = (cid) => menu.filter((m) => m.category_id === cid).length;

  return (
    <section>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="font-display text-2xl">Menu — Showing {visible.length} of {menu.length}</h2>
        <div className="flex items-center gap-2">
          {missingCount > 0 && (
            <button
              onClick={() => setOnlyMissing((v) => !v)}
              data-testid="filter-missing-photo"
              className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full"
              style={{
                background: onlyMissing ? "var(--accent)" : "transparent",
                color: onlyMissing ? "var(--accent-fg)" : "var(--accent)",
                border: `1px solid var(--accent)`,
              }}
              title={onlyMissing ? "Show all dishes" : "Show only dishes missing a photo"}
            >
              <ImagePlus className="w-3.5 h-3.5" />
              {onlyMissing ? `Showing ${missingCount} missing photos` : `${missingCount} need${missingCount === 1 ? "s" : ""} a photo`}
            </button>
          )}
          <button onClick={onCreate} data-testid="add-item-btn" className="gold-btn rounded-full px-4 py-2 text-sm font-semibold inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add dish</button>
        </div>
      </div>

      <div className="relative mb-3 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--fg-muted)" }} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search dishes by name, ingredient or tag…"
          data-testid="menu-search"
          className="w-full rounded-xl pl-9 pr-9 py-2 text-sm bg-transparent focus:outline-none focus:border-[var(--accent)]"
          style={{ border: "1px solid var(--border)" }}
        />
        {query && (
          <button onClick={() => setQuery("")} data-testid="menu-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center hover:bg-white/5">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {cats.length > 0 && (
        <div className="flex gap-2 flex-wrap mb-3" data-testid="menu-cat-chips">
          <CatChip active={activeCat === ""} onClick={() => setActiveCat("")} label="All" count={menu.length} testid="menu-cat-all" />
          {cats.map((c) => (
            <CatChip
              key={c.id}
              active={activeCat === c.id}
              onClick={() => setActiveCat((cur) => cur === c.id ? "" : c.id)}
              label={c.name}
              count={countFor(c.id)}
              testid={`menu-cat-${c.id}`}
            />
          ))}
        </div>
      )}

      <div className="flex gap-2 flex-wrap mb-4" data-testid="menu-quick-filters">
        <QuickChip active={quick === "veg"} onClick={() => setQuick(quick === "veg" ? "" : "veg")} icon={<Leaf className="w-3 h-3" />} label="Veg" tone="emerald" testid="menu-quick-veg" />
        <QuickChip active={quick === "non-veg"} onClick={() => setQuick(quick === "non-veg" ? "" : "non-veg")} icon={<Drumstick className="w-3 h-3" />} label="Non-veg" tone="red" testid="menu-quick-nonveg" />
        <QuickChip active={quick === "featured"} onClick={() => setQuick(quick === "featured" ? "" : "featured")} icon={<Star className="w-3 h-3" />} label="Featured" tone="accent" testid="menu-quick-featured" />
        <QuickChip active={quick === "sold-out"} onClick={() => setQuick(quick === "sold-out" ? "" : "sold-out")} icon={<X className="w-3 h-3" />} label="Sold out" tone="red" testid="menu-quick-soldout" />
        {hasFilter && (
          <button onClick={clearFilters} data-testid="menu-clear-filters" className="text-xs px-3 py-1 rounded-full hover:opacity-90" style={{ border: "1px solid var(--border)", color: "var(--fg-muted)" }}>Clear all</button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {visible.map((m) => {
          const r = ratingsById[m.id] || {};
          const missing = !hasItemImage(m);
          return (
            <div key={m.id} className="p-3 rounded-2xl flex gap-3" style={{ border: missing ? "1px dashed var(--accent)" : "1px solid var(--border)", background: "var(--bg-card)" }} data-testid={`own-item-${m.id}`}>
              <button
                onClick={() => onEdit(m)}
                className="relative shrink-0 group rounded-xl overflow-hidden"
                title={missing ? "Click to add a photo" : "Edit photos"}
                data-testid={`thumb-${m.id}`}
              >
                <img src={dishImg(m)} onError={onDishImgError} alt={m.name} className="w-20 h-20 rounded-xl object-cover" />
                {missing && (
                  <span
                    className="absolute inset-0 flex flex-col items-center justify-center text-[9px] font-mono uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: "rgba(229,169,60,0.85)", color: "#0A0A0C" }}
                  >
                    <ImagePlus className="w-4 h-4 mb-0.5" />
                    Add photo
                  </span>
                )}
                {(m.images?.length || 0) > 1 && <span className="absolute -top-1 -right-1 text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: "rgba(0,0,0,0.7)", color: "#fff" }}>+{m.images.length - 1}</span>}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <div className="font-display text-base truncate">{m.name}</div>
                  {m.is_veg && <Leaf className="w-3 h-3 text-emerald-500" />}
                  {m.is_veg === false && <Drumstick className="w-3 h-3 text-red-500" />}
                  {m.is_spicy && <Spicy className="w-3 h-3 text-orange-500" />}
                  {m.is_chef_special && <Award className="w-3 h-3" style={{ color: "var(--accent)" }} />}
                </div>
                <div className="text-xs font-mono" style={{ color: "var(--fg-muted)" }}>₹{m.price}</div>
                <div className="flex items-center gap-1 mt-1">
                  {r.rating_count ? (
                    <>
                      {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-3 h-3 ${n <= Math.round(r.avg_rating) ? "fill-[#E5A93C]" : ""}`} style={{ color: n <= Math.round(r.avg_rating) ? "var(--accent)" : "var(--fg-dim)" }} />)}
                      <span className="text-[11px] font-mono">{r.avg_rating?.toFixed(1)}</span>
                      <span className="text-[10px]" style={{ color: "var(--fg-dim)" }}>({r.rating_count})</span>
                    </>
                  ) : <span className="text-[10px]" style={{ color: "var(--fg-dim)" }}>No ratings yet</span>}
                </div>
                <div className="text-[10px] mt-1 flex gap-1 flex-wrap">
                  {missing && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full" style={{ background: "rgba(229,169,60,0.15)", color: "var(--accent)", border: "1px solid var(--accent)" }}>No photo</span>}
                  {m.is_featured && <span style={{ color: "var(--accent)" }}>★ Featured</span>}
                  {m.is_sold_out && <span className="text-red-400">Sold out</span>}
                </div>
                <div className="mt-2 flex gap-2 flex-wrap">
                  <button onClick={() => onEdit(m)} data-testid={`edit-${m.id}`} className="text-xs flex items-center gap-1 px-2 py-1 rounded-full hover:bg-white/5" style={{ border: "1px solid var(--border)" }}><Pencil className="w-3 h-3" /> Edit</button>
                  {missing && (
                    <button onClick={() => onEdit(m)} data-testid={`add-photo-${m.id}`} className="text-xs flex items-center gap-1 px-2 py-1 rounded-full font-semibold" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>
                      <ImagePlus className="w-3 h-3" /> Add photo
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {visible.length === 0 && (
          <div className="col-span-full py-10 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
            {hasFilter ? "No dishes match the current filters." : "No dishes yet — add your first one."}
          </div>
        )}
      </div>
    </section>
  );
}

function CatChip({ active, onClick, label, count, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="text-xs px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5"
      style={{
        background: active ? "var(--accent)" : "transparent",
        color: active ? "var(--accent-fg)" : "var(--fg)",
        border: `1px solid ${active ? "var(--accent)" : "var(--border)"}`,
      }}
    >
      {label}
      <span
        className="text-[10px] px-1.5 py-0.5 rounded-full font-mono"
        style={{ background: active ? "rgba(0,0,0,0.15)" : "var(--bg-muted)", color: active ? "var(--accent-fg)" : "var(--fg-muted)" }}
      >{count}</span>
    </button>
  );
}

function QuickChip({ active, onClick, icon, label, tone = "accent", testid }) {
  const palette = {
    emerald: { bg: "#10b981", fg: "#000", border: "#10b981" },
    red: { bg: "#ef4444", fg: "#fff", border: "#ef4444" },
    accent: { bg: "var(--accent)", fg: "var(--accent-fg)", border: "var(--accent)" },
  }[tone];
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="text-xs px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5"
      style={{
        background: active ? palette.bg : "transparent",
        color: active ? palette.fg : "var(--fg)",
        border: `1px solid ${active ? palette.border : "var(--border)"}`,
      }}
    >
      {icon}{label}
    </button>
  );
}

function MarginTab({ margin }) {
  return (
    <section>
      <h2 className="font-display text-2xl mb-4">Menu Engineering Matrix</h2>
      <p className="text-sm mb-4" style={{ color: "var(--fg-muted)" }}>Stars = popular & high margin. Dogs = low both. Prune dogs, celebrate stars.</p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="margin-table">
          <thead className="text-[11px] font-mono uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>
            <tr style={{ borderBottom: "1px solid var(--border)" }}>
              <th className="text-left py-2 px-3">Dish</th>
              <th className="text-right py-2 px-3">Price</th>
              <th className="text-right py-2 px-3">Margin</th>
              <th className="text-right py-2 px-3">Orders</th>
              <th className="text-right py-2 px-3">Class</th>
            </tr>
          </thead>
          <tbody>
            {margin.map((m) => (
              <tr key={m.id} style={{ borderBottom: "1px solid var(--border)" }} className="hover:bg-white/5">
                <td className="py-2 px-3 flex items-center gap-2"><img src={dishImg(m)} onError={onDishImgError} className="w-8 h-8 rounded-lg object-cover" alt="" /> {m.name}</td>
                <td className="text-right font-mono">₹{m.price}</td>
                <td className="text-right font-mono">{m.margin_pct}%</td>
                <td className="text-right font-mono">{m.times_ordered}</td>
                <td className="text-right"><ClassTag cls={m.class} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RatingsTab({ ratings }) {
  return (
    <section>
      <h2 className="font-display text-2xl mb-4">Dish Quality Heatmap</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ratings-grid">
        {ratings.map((r) => {
          const color = r.avg_rating >= 4.5 ? "rgba(16,185,129,0.4)" : r.avg_rating >= 3 ? "rgba(245,158,11,0.4)" : r.rating_count === 0 ? "var(--border)" : "rgba(239,68,68,0.4)";
          return (
            <div key={r.id} className="p-3 rounded-2xl flex gap-3 items-center" style={{ border: `1px solid ${color}`, background: "var(--bg-card)" }}>
              <img src={dishImg(r)} onError={onDishImgError} className="w-14 h-14 rounded-xl object-cover" alt="" />
              <div className="flex-1 min-w-0">
                <div className="font-display text-base truncate">{r.name}</div>
                <div className="text-xs font-mono" style={{ color: "var(--fg-muted)" }}>{r.rating_count} ratings</div>
              </div>
              <div className="text-right">
                <div className="font-display text-2xl" style={{ color: "var(--accent)" }}>{r.avg_rating?.toFixed?.(1) || "—"}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--fg-dim)" }}>/5</div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function KPI({ label, value }) {
  return (
    <div className="p-4 rounded-2xl" style={{ border: "1px solid var(--border)", background: "var(--bg-card)" }}>
      <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
    </div>
  );
}

function ClassTag({ cls }) {
  const map = {
    Star: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    Plowhorse: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    Puzzle: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    Dog: "bg-red-500/15 text-red-400 border-red-500/30",
  };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border font-mono uppercase tracking-widest ${map[cls]}`}>{cls}</span>;
}

/* ---------- Categories tab ---------- */
function CategoriesTab({ cats, onReload }) {
  const [name, setName] = useState("");
  const [order, setOrder] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const create = async () => {
    if (!name.trim()) return;
    try { await api.post("/owner/categories", { name, sort_order: Number(order) || 0 }); toast.success("Category added"); setName(""); setOrder(0); onReload(); }
    catch { toast.error("Could not create"); }
  };
  const rename = async (id) => {
    try { await api.put(`/owner/categories/${id}`, { name: editName }); setEditingId(null); onReload(); toast.success("Renamed"); }
    catch { toast.error("Rename failed"); }
  };
  const del = async (id) => {
    if (!confirm("Delete this category?")) return;
    try { await api.delete(`/owner/categories/${id}`); toast.success("Deleted"); onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <section>
      <h2 className="font-display text-2xl mb-4">Categories — {cats.length}</h2>
      <div className="p-4 rounded-2xl flex gap-2 mb-4" style={{ border: "1px solid var(--border)", background: "var(--bg-card)" }} data-testid="cat-add-row">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New category name (e.g. Bread Basket)" data-testid="cat-name-input" className="flex-1 bg-transparent rounded-xl px-3 py-2 focus:outline-none" style={{ border: "1px solid var(--border)" }} />
        <input value={order} onChange={(e) => setOrder(e.target.value)} type="number" placeholder="Order" className="w-20 bg-transparent rounded-xl px-3 py-2" style={{ border: "1px solid var(--border)" }} data-testid="cat-order-input" />
        <button onClick={create} className="gold-btn rounded-full px-4 font-semibold" data-testid="cat-add-btn"><Plus className="w-4 h-4 inline mr-1" /> Add</button>
      </div>
      <div className="space-y-2">
        {cats.map((c) => (
          <div key={c.id} className="p-3 rounded-xl flex items-center gap-3" style={{ border: "1px solid var(--border)", background: "var(--bg-card)" }} data-testid={`cat-row-${c.id}`}>
            <Tag className="w-4 h-4" style={{ color: "var(--accent)" }} />
            {editingId === c.id ? (
              <>
                <input value={editName} onChange={(e) => setEditName(e.target.value)} className="flex-1 bg-transparent rounded-lg px-2 py-1.5" style={{ border: "1px solid var(--border)" }} data-testid={`cat-edit-${c.id}`} />
                <button onClick={() => rename(c.id)} className="text-xs gold-btn rounded-full px-3 py-1" data-testid={`cat-save-${c.id}`}>Save</button>
                <button onClick={() => setEditingId(null)} className="text-xs opacity-70">Cancel</button>
              </>
            ) : (
              <>
                <div className="flex-1">
                  <div className="font-display text-base">{c.name}</div>
                  <div className="text-[10px] font-mono" style={{ color: "var(--fg-muted)" }}>order: {c.sort_order}</div>
                </div>
                <button onClick={() => { setEditingId(c.id); setEditName(c.name); }} className="text-xs hover:opacity-100 opacity-70" data-testid={`cat-rename-${c.id}`}><Pencil className="w-3 h-3 inline" /> Rename</button>
                <button onClick={() => del(c.id)} className="text-xs text-red-400 hover:text-red-300" data-testid={`cat-del-${c.id}`}><Trash2 className="w-3 h-3 inline" /></button>
              </>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- CRM + Loyalty + CustomerDetail: (re-use from previous code) ---------- */
function CRMTab({ customers, onOpenDetail }) {
  const [q, setQ] = useState("");
  const [minVisits, setMinVisits] = useState(0);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [showAll, setShowAll] = useState(false);

  const filtered = customers
    .filter((c) => {
      if (minVisits && c.visits < minVisits) return false;
      if (q) {
        const s = q.toLowerCase();
        if (!((c.name || "").toLowerCase().includes(s) || (c.phone || "").toLowerCase().includes(s) || (c.email || "").toLowerCase().includes(s))) return false;
      }
      if (from || to) {
        const lv = c.last_visit ? new Date(c.last_visit) : null;
        if (!lv) return false;
        if (from && lv < new Date(from)) return false;
        if (to && lv > new Date(new Date(to).setHours(23, 59, 59, 999))) return false;
      }
      return true;
    })
    .sort((a, b) => new Date(b.last_visit || 0) - new Date(a.last_visit || 0)); // most-recent first

  const visible = showAll ? filtered : filtered.slice(0, 10);
  const hasMore = filtered.length > visible.length;
  const filtersActive = q || minVisits || from || to;

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("crave_token");
      const rid = localStorage.getItem("crave_active_rid") || "";
      const res = await fetch(`${BACKEND}/api/owner/customers/export/csv`, { headers: { Authorization: `Bearer ${token}`, "X-Restaurant-Id": rid } });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "customers.csv"; a.click(); URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Download failed"); }
  };

  const clearFilters = () => { setQ(""); setMinVisits(0); setFrom(""); setTo(""); };

  return (
    <section>
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-display text-2xl">Customer CRM — {visible.length}/{filtered.length}</h2>
        <button onClick={downloadCsv} data-testid="crm-export" className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full hover:bg-white/5" style={{ color: "var(--accent)", border: "1px solid var(--accent)" }}>
          <Download className="w-3.5 h-3.5" /> Export Excel / CSV
        </button>
      </div>
      <div className="p-3 rounded-2xl mb-4 flex flex-col gap-2" style={{ border: "1px solid var(--border)", background: "var(--bg-card)" }}>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--fg-dim)" }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone or email" data-testid="crm-search" className="w-full pl-9 bg-transparent rounded-xl px-3 py-2 focus:outline-none" style={{ border: "1px solid var(--border)" }} />
          </div>
          <select value={minVisits} onChange={(e) => setMinVisits(Number(e.target.value))} data-testid="crm-min-visits" className="rounded-xl px-3 py-2" style={{ background: "var(--bg-muted)", border: "1px solid var(--border)" }}>
            <option value={0}>All visits</option>
            <option value={1}>1+ visits</option>
            <option value={3}>3+ visits (regulars)</option>
            <option value={5}>5+ visits (VIPs)</option>
          </select>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] sm:mr-1" style={{ color: "var(--fg-muted)" }}>Last visit</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="crm-date-from" className="rounded-xl px-3 py-2 text-sm" style={{ background: "var(--bg-muted)", border: "1px solid var(--border)", colorScheme: "dark" }} />
          <span className="text-xs" style={{ color: "var(--fg-muted)" }}>to</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="crm-date-to" className="rounded-xl px-3 py-2 text-sm" style={{ background: "var(--bg-muted)", border: "1px solid var(--border)", colorScheme: "dark" }} />
          {filtersActive ? (
            <button onClick={clearFilters} data-testid="crm-clear-filters" className="text-xs ml-auto px-3 py-1.5 rounded-full hover:bg-white/5" style={{ border: "1px solid var(--border)" }}>Clear filters</button>
          ) : null}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="crm-table">
          <thead className="text-[11px] font-mono uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>
            <tr style={{ borderBottom: "1px solid var(--border)" }}>
              <th className="text-left py-2 px-3">Guest</th><th className="text-left py-2 px-3">Phone</th><th className="text-left py-2 px-3">Email</th>
              <th className="text-right py-2 px-3">Visits</th><th className="text-right py-2 px-3">Avg spend</th>
              <th className="text-right py-2 px-3">Total spend</th><th className="text-right py-2 px-3">Last visit</th><th></th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && <tr><td colSpan={8} className="py-6 text-center opacity-60">No customers match.</td></tr>}
            {visible.map((c) => (
              <tr key={c.id} className="hover:bg-white/5 cursor-pointer" style={{ borderBottom: "1px solid var(--border)" }} onClick={() => onOpenDetail(c.id)} data-testid={`crm-row-${c.id}`}>
                <td className="py-2 px-3">{c.name}</td>
                <td className="py-2 px-3 font-mono" style={{ color: "var(--fg-muted)" }}>{c.phone}</td>
                <td className="py-2 px-3 font-mono" style={{ color: "var(--fg-muted)" }}>{c.email || "—"}</td>
                <td className="py-2 px-3 text-right font-mono">{c.visits}</td>
                <td className="py-2 px-3 text-right font-mono">₹{c.avg_spend?.toFixed?.(0)}</td>
                <td className="py-2 px-3 text-right font-mono" style={{ color: "var(--accent)" }}>₹{c.spend?.toFixed?.(0)}</td>
                <td className="py-2 px-3 text-right font-mono" style={{ color: "var(--fg-muted)" }}>{c.last_visit ? new Date(c.last_visit).toLocaleDateString() : "—"}</td>
                <td className="py-2 px-3 text-right" style={{ color: "var(--fg-dim)" }}><ChevronRight className="w-4 h-4 inline" /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {hasMore && (
          <div className="flex justify-center mt-4">
            <button onClick={() => setShowAll(true)} data-testid="crm-show-all" className="text-xs px-4 py-2 rounded-full hover:bg-white/5" style={{ color: "var(--accent)", border: "1px solid var(--accent)" }}>
              Show all {filtered.length} customers
            </button>
          </div>
        )}
        {showAll && filtered.length > 10 && (
          <div className="flex justify-center mt-4">
            <button onClick={() => setShowAll(false)} data-testid="crm-show-recent" className="text-xs px-4 py-2 rounded-full hover:bg-white/5" style={{ border: "1px solid var(--border)" }}>
              Show only 10 most recent
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function CustomerDetailModal({ customerId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => { try { const { data } = await api.get(`/owner/customers/${customerId}`); setData(data); } catch { toast.error("Could not load"); onClose(); } })(); }, [customerId, onClose]);
  if (!data) return null;
  const { customer, orders, reviews, stats } = data;
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl rounded-3xl p-6 max-h-[90vh] overflow-y-auto" style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }} data-testid="customer-detail-modal">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: "var(--fg-muted)" }}>Guest profile</div>
            <h3 className="font-display text-3xl">{customer.name}</h3>
            <div className="text-sm mt-1 font-mono" style={{ color: "var(--fg-muted)" }}>{customer.phone} · {customer.email || "no email"}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "var(--glass-light)" }} data-testid="close-customer-detail"><X className="w-4 h-4" /></button>
        </div>
        <div className="mt-5 grid grid-cols-3 gap-3">
          <KPI label="Visits" value={stats.visits} />
          <KPI label="Total spend" value={`₹${stats.total_spend?.toFixed?.(0)}`} />
          <KPI label="Avg rating given" value={stats.avg_rating_given || "—"} />
        </div>
        {customer.preferences?.allergens?.length > 0 && (
          <div className="mt-4 p-3 rounded-xl text-xs" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#FCA5A5" }}>
            ⚠ Allergens: {customer.preferences.allergens.join(", ")}
          </div>
        )}
        <h4 className="mt-6 font-display text-xl">Order invoices</h4>
        <div className="mt-3 space-y-3" data-testid="customer-orders">
          {orders.length === 0 && <div className="text-sm opacity-60">No orders yet.</div>}
          {orders.map((o) => (
            <div key={o.id} className="p-4 rounded-2xl" style={{ border: "1px solid var(--border)", background: "var(--bg-muted)" }}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>#{o.id.slice(0, 8)} · Table {o.table_no} · {new Date(o.created_at).toLocaleString()}</div>
                  <div className="font-display text-lg">{o.items.length} items</div>
                </div>
                <span className={`text-[10px] px-2 py-1 rounded-full font-mono uppercase tracking-widest ${o.paid ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-300"}`}>{o.status}</span>
              </div>
              <div className="mt-3 space-y-1 text-sm">
                {o.items.map((i, idx) => (<div key={idx} className="flex justify-between"><span>{i.qty}× {i.name}{i.complimentary ? " (complimentary)" : ""}{i.notes ? ` — ${i.notes}` : ""}</span><span className="font-mono">{i.complimentary ? "Free" : `₹${(i.price * i.qty).toFixed(0)}`}</span></div>))}
              </div>
              <div className="mt-3 pt-2 text-sm space-y-0.5" style={{ borderTop: "1px solid var(--border)" }}>
                {o.subtotal != null && <Row label="Subtotal" value={`₹${o.subtotal?.toFixed?.(0)}`} />}
                {o.discount ? <Row label={`Loyalty discount (${o.loyalty_applied?.discount_pct || 0}%)`} value={`-₹${o.discount?.toFixed?.(0)}`} /> : null}
                <Row label={<span className="font-display">Total</span>} value={<span className="font-display" style={{ color: "var(--accent)" }}>₹{o.total?.toFixed?.(0)}</span>} />
              </div>
            </div>
          ))}
        </div>
        {reviews.length > 0 && (<><h4 className="mt-6 font-display text-xl">Ratings given</h4>
          <div className="mt-3 space-y-1 text-sm">
            {reviews.map((r) => (<div key={r.id} className="flex items-center justify-between p-2 rounded-lg" style={{ background: "var(--glass-light)" }}><span>{r.comment || "—"}</span><span className="flex gap-0.5">{[1,2,3,4,5].map((n) => <Star key={n} className="w-3.5 h-3.5" style={{ color: n <= r.rating ? "var(--accent)" : "var(--fg-dim)", fill: n <= r.rating ? "var(--accent)" : "transparent" }} />)}</span></div>))}
          </div></>)}
      </div>
    </div>
  );
}

function Row({ label, value }) { return <div className="flex items-center justify-between text-sm"><span style={{ color: "var(--fg-muted)" }}>{label}</span><span className="font-mono">{value}</span></div>; }

/* ---------- Loyalty tab ---------- */
function LoyaltyTab({ loyalty, menu, onSaved }) {
  const [form, setForm] = useState({
    enabled: !!loyalty.enabled,
    visit_threshold: loyalty.visit_threshold ?? 3,
    discount_pct: loyalty.discount_pct ?? 10,
    complimentary_item_id: loyalty.complimentary_item_id || "",
    message: loyalty.message || "",
  });
  const save = async () => {
    try { await api.put("/owner/loyalty", { ...form, complimentary_item_id: form.complimentary_item_id || null }); toast.success("Loyalty settings saved"); onSaved(); }
    catch { toast.error("Save failed"); }
  };
  return (
    <section className="max-w-2xl">
      <h2 className="font-display text-2xl mb-1">Loyalty & Perks</h2>
      <p className="text-sm mb-5" style={{ color: "var(--fg-muted)" }}>Reward repeat guests — auto-apply discounts and complimentary items at checkout. Customers are notified before placing the order.</p>
      <div className="p-5 rounded-2xl space-y-4" style={{ border: "1px solid var(--border)", background: "var(--bg-card)" }} data-testid="loyalty-form">
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="loy-enabled" />
          <div><div className="font-display text-lg">Enable Regular perks</div><div className="text-xs" style={{ color: "var(--fg-muted)" }}>Auto-reward returning guests when they dine again</div></div>
        </label>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input label="Unlock after (visits)" type="number" value={form.visit_threshold} onChange={(v) => setForm({ ...form, visit_threshold: Number(v) || 0 })} testid="loy-threshold" />
          <Input label="Discount %" type="number" value={form.discount_pct} onChange={(v) => setForm({ ...form, discount_pct: Number(v) || 0 })} testid="loy-discount" />
        </div>
        <div>
          <label className="text-xs" style={{ color: "var(--fg-muted)" }}>Complimentary dish (optional)</label>
          <select value={form.complimentary_item_id || ""} onChange={(e) => setForm({ ...form, complimentary_item_id: e.target.value })} data-testid="loy-comp" className="w-full rounded-xl px-3 py-2.5" style={{ background: "var(--bg-muted)", border: "1px solid var(--border)" }}>
            <option value="">— none —</option>
            {menu.map((m) => <option key={m.id} value={m.id}>{m.name} (₹{m.price})</option>)}
          </select>
        </div>
        <Area label="Message shown to the guest" value={form.message} onChange={(v) => setForm({ ...form, message: v })} testid="loy-msg" />
        <button onClick={save} data-testid="loy-save" className="gold-btn rounded-full px-6 py-2.5 font-semibold inline-flex items-center gap-2"><Gift className="w-4 h-4" /> Save loyalty config</button>
      </div>
    </section>
  );
}

/* ---------- Tables & QR tab ---------- */

/* ---------- Guest Wishlist (AI sommelier "unavailable" requests) ---------- */

/* ---------- AI Usage tab (token input/output + daily bar chart) ---------- */
function AIUsageTab({ rid }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(14);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/owner/llm-usage?days=${days}`);
        if (!cancelled) setData(data);
      } catch { toast.error("Could not load AI usage"); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [days, rid]);

  const total = useMemo(() => {
    if (!data) return 0;
    return (data.totals?.input || 0) + (data.totals?.output || 0);
  }, [data]);
  const monthTotal = useMemo(() => {
    if (!data) return 0;
    return (data.this_month?.input || 0) + (data.this_month?.output || 0);
  }, [data]);
  const limit = data?.limit_monthly;
  const pct = limit && limit > 0 ? Math.min(100, (monthTotal / limit) * 100) : null;

  const fmt = (n) => (n || 0).toLocaleString();

  return (
    <section data-testid="ai-usage-tab">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl flex items-center gap-2">
            <Sparkles className="w-5 h-5" style={{ color: "var(--accent)" }} />
            AI Sommelier usage
          </h2>
          <p className="text-xs mt-1 max-w-xl" style={{ color: "var(--fg-muted)" }}>
            Tokens consumed by guest chats with the AI Sommelier. Counts are reported directly by Anthropic for each call. Super admin sets the monthly cap.
          </p>
        </div>
        <div className="inline-flex rounded-full p-1" style={{ background: "var(--bg-muted)", border: "1px solid var(--border)" }}>
          {[7, 14, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`ai-usage-range-${d}`}
              className="text-xs px-3 py-1.5 rounded-full font-semibold"
              style={{
                background: days === d ? "var(--accent)" : "transparent",
                color: days === d ? "var(--accent-fg)" : "var(--fg-muted)",
              }}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="mt-6 opacity-60 text-sm">Loading…</p>}

      {!loading && data && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
            <UsageCard label="Input tokens (all time)" value={fmt(data.totals?.input)} testid="usage-total-input" />
            <UsageCard label="Output tokens (all time)" value={fmt(data.totals?.output)} testid="usage-total-output" />
            <UsageCard label="Chats (all time)" value={fmt(data.totals?.calls)} testid="usage-total-calls" />
            <UsageCard label="This month (in + out)" value={fmt(monthTotal)} accent testid="usage-month-total" />
          </div>

          {limit != null && (
            <div className="mt-5 p-4 rounded-2xl" style={{ border: "1px solid var(--border)", background: "var(--bg-card)" }} data-testid="usage-limit-card">
              <div className="flex items-baseline justify-between">
                <div className="font-display text-lg">Monthly cap</div>
                <div className="text-xs font-mono" style={{ color: pct >= 90 ? "rgb(248,113,113)" : "var(--fg-muted)" }}>
                  {fmt(monthTotal)} / {fmt(limit)} tokens
                </div>
              </div>
              <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: "var(--glass-light)" }}>
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${pct}%`,
                    background: pct >= 90 ? "rgb(239,68,68)" : pct >= 70 ? "rgb(234,179,8)" : "var(--accent)",
                  }}
                />
              </div>
              {pct >= 90 && (
                <div className="text-xs mt-2" style={{ color: "rgb(248,113,113)" }}>
                  You're close to the monthly cap — extra guest chats will fall back to the offline recommender. Contact the platform admin to raise your limit.
                </div>
              )}
            </div>
          )}

          {limit == null && total > 0 && (
            <div className="mt-5 p-3 rounded-xl text-xs" style={{ border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--fg-muted)" }}>
              No monthly cap set — this restaurant currently has unlimited AI Sommelier usage. The platform admin can configure a cap any time.
            </div>
          )}

          <div className="mt-6 p-4 rounded-2xl" style={{ border: "1px solid var(--border)", background: "var(--bg-card)" }} data-testid="usage-chart">
            <div className="flex items-baseline justify-between">
              <div className="font-display text-lg">Daily usage — last {days} days</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: "var(--fg-muted)" }}>
                input vs output
              </div>
            </div>
            <div className="h-64 mt-3">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.daily || []}>
                  <CartesianGrid stroke="rgba(128,128,128,0.15)" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="day"
                    tickFormatter={(d) => d.slice(5)}
                    stroke="rgba(128,128,128,0.8)"
                    fontSize={11}
                  />
                  <YAxis stroke="rgba(128,128,128,0.8)" fontSize={11} />
                  <Tooltip
                    contentStyle={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 12 }}
                    labelStyle={{ color: "var(--fg)" }}
                  />
                  <Legend />
                  <Bar dataKey="input" stackId="t" fill="#E5A93C" name="Input" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="output" stackId="t" fill="#7C3AED" name="Output" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {(!data.daily || data.daily.every((d) => (d.input + d.output) === 0)) && (
            <div
              className="mt-6 p-6 rounded-2xl text-center"
              style={{ border: "1px dashed var(--border)", color: "var(--fg-muted)" }}
              data-testid="ai-usage-empty"
            >
              <Sparkles className="w-5 h-5 mx-auto mb-2" style={{ color: "var(--accent)" }} />
              No AI Sommelier chats yet. When guests start asking, usage will appear here.
            </div>
          )}
        </>
      )}
    </section>
  );
}

function UsageCard({ label, value, accent, testid }) {
  return (
    <div
      className="p-4 rounded-2xl"
      style={{
        border: `1px solid ${accent ? "var(--accent)" : "var(--border)"}`,
        background: accent ? "rgba(229,169,60,0.08)" : "var(--bg-card)",
      }}
      data-testid={testid}
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: "var(--fg-muted)" }}>
        {label}
      </div>
      <div className="font-display text-3xl mt-1" style={{ color: accent ? "var(--accent)" : "var(--fg)" }}>
        {value}
      </div>
    </div>
  );
}


function WishlistTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open"); // open | resolved | all

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/owner/menu-requests"); setData(data); }
    catch { toast.error("Could not load guest wishlist"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resolve = async (g, resolved) => {
    try {
      await api.put(`/owner/menu-requests/${encodeURIComponent(g.key)}/resolve`, { resolved });
      toast.success(resolved ? `Marked "${g.requested_dish}" resolved` : "Reopened");
      load();
    } catch { toast.error("Could not update"); }
  };

  const groups = useMemo(() => {
    if (!data) return [];
    return (data.groups || []).filter((g) => {
      if (filter === "open") return !g.resolved;
      if (filter === "resolved") return g.resolved;
      return true;
    });
  }, [data, filter]);

  const totalOpen = useMemo(() => (data?.groups || []).filter((g) => !g.resolved).length, [data]);

  return (
    <section data-testid="wishlist-tab">
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl flex items-center gap-2">
            <Lightbulb className="w-5 h-5" style={{ color: "var(--accent)" }} />
            Guest Wishlist
          </h2>
          <p className="text-xs mt-1 max-w-xl" style={{ color: "var(--fg-muted)" }}>
            Every time a guest asked our AI Sommelier for a dish we don't currently serve, it lands here — so you can see what the room is actually craving and decide what to add next.
          </p>
        </div>
        <div className="inline-flex rounded-full p-1" style={{ background: "var(--bg-muted)", border: "1px solid var(--border)" }}>
          {[
            { key: "open", label: `Open (${totalOpen})` },
            { key: "resolved", label: "Resolved" },
            { key: "all", label: "All" },
          ].map((f) => {
            const on = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`wishlist-filter-${f.key}`}
                className="text-xs px-3 py-1.5 rounded-full font-semibold"
                style={{
                  background: on ? "var(--accent)" : "transparent",
                  color: on ? "var(--accent-fg)" : "var(--fg-muted)",
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading && <p className="mt-4 opacity-60 text-sm">Loading…</p>}

      {!loading && groups.length === 0 && (
        <div
          className="mt-6 p-6 rounded-2xl text-center"
          style={{ border: "1px dashed var(--border)", color: "var(--fg-muted)" }}
          data-testid="wishlist-empty"
        >
          <Sparkles className="w-5 h-5 mx-auto mb-2" style={{ color: "var(--accent)" }} />
          {filter === "open"
            ? "No open wishlist items — your menu is covering what guests ask for."
            : filter === "resolved"
              ? "No resolved items yet."
              : "No wishlist entries yet."}
        </div>
      )}

      <div className="mt-4 space-y-3" data-testid="wishlist-list">
        {groups.map((g) => (
          <div
            key={g.key}
            className="p-4 rounded-2xl"
            style={{ background: "var(--bg-card)", border: `1px solid ${g.resolved ? "rgba(16,185,129,0.35)" : "var(--border)"}` }}
            data-testid={`wishlist-item-${g.key}`}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display text-xl capitalize">{g.requested_dish}</div>
                  <span
                    className="text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-0.5 rounded-full"
                    style={{ background: "rgba(229,169,60,0.15)", color: "var(--accent)" }}
                  >
                    asked {g.count}×
                  </span>
                  {g.resolved && (
                    <span className="text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-0.5 rounded-full text-emerald-400" style={{ background: "rgba(16,185,129,0.15)" }}>
                      resolved
                    </span>
                  )}
                </div>
                <div className="text-[11px] font-mono mt-1" style={{ color: "var(--fg-dim)" }}>
                  latest ask: {g.last_customer_name || "Guest"} · {new Date(g.last_requested_at).toLocaleString()}
                </div>
                <div className="mt-3 text-sm italic" style={{ color: "var(--fg-muted)" }}>
                  “{g.last_query}”
                </div>
                {g.samples && g.samples.length > 1 && (
                  <details className="mt-2">
                    <summary className="text-[11px] cursor-pointer" style={{ color: "var(--fg-muted)" }}>
                      {g.samples.length - 1} more recent ask{g.samples.length > 2 ? "s" : ""}
                    </summary>
                    <div className="mt-2 space-y-1 text-[12px] pl-3 border-l" style={{ borderColor: "var(--border)" }}>
                      {g.samples.slice(1).map((s, i) => (
                        <div key={i} style={{ color: "var(--fg-muted)" }}>
                          <span className="italic">“{s.query}”</span>
                          <span className="ml-2 text-[10px] font-mono" style={{ color: "var(--fg-dim)" }}>
                            · {s.customer_name || "Guest"} · {new Date(s.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                {g.resolved ? (
                  <button
                    onClick={() => resolve(g, false)}
                    data-testid={`wishlist-reopen-${g.key}`}
                    className="text-xs px-3 py-1.5 rounded-full hover:bg-white/5"
                    style={{ border: "1px solid var(--border)", color: "var(--fg-muted)" }}
                  >
                    Reopen
                  </button>
                ) : (
                  <button
                    onClick={() => resolve(g, true)}
                    data-testid={`wishlist-resolve-${g.key}`}
                    className="text-xs gold-btn rounded-full px-3 py-1.5 font-semibold inline-flex items-center gap-1"
                  >
                    <Check className="w-3 h-3" /> Mark resolved
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function QRTab({ resto }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/owner/qr-codes"); setData(r.data); }
    catch { toast.error("Could not load QR codes"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [resto?.id, resto?.num_tables]);

  const downloadOne = (qr) => {
    const a = document.createElement("a");
    a.href = qr.image_data_url;
    a.download = `${resto.slug}-table-${qr.table}.png`;
    a.click();
  };

  const printAll = () => {
    if (!data?.qr_codes?.length) return;
    const win = window.open("", "_blank");
    if (!win) { toast.error("Pop-up blocked — allow pop-ups to print"); return; }
    const cards = data.qr_codes.map((q) => `
      <div class="card">
        <div class="brand">MnuVerse · ${data.restaurant_name}</div>
        <img src="${q.image_data_url}" />
        <div class="tlabel">${q.label}</div>
        <div class="caption">Scan to order · Pay table-side</div>
        <div class="url">${q.url}</div>
      </div>`).join("");
    win.document.write(`<!doctype html><html><head><title>QR — ${data.restaurant_name}</title>
      <style>
        body{font-family:Georgia,serif;background:#f6f4ec;margin:0;padding:24px;}
        .grid{display:grid;grid-template-columns:repeat(2,1fr);gap:24px;}
        .card{background:#fff;border:1px solid #ddd;border-radius:18px;padding:24px;text-align:center;page-break-inside:avoid;}
        .brand{font-size:11px;letter-spacing:0.25em;text-transform:uppercase;color:#a17b2c;margin-bottom:12px;}
        .tlabel{font-size:28px;margin-top:14px;color:#0a0a0c;}
        .caption{font-size:11px;color:#666;margin-top:6px;}
        .url{font-family:monospace;font-size:9px;color:#999;margin-top:10px;word-break:break-all;}
        img{width:240px;height:240px;}
        @media print{body{padding:0}}
      </style></head><body><div class="grid">${cards}</div></body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 400);
  };

  if (loading) return <section><h2 className="font-display text-2xl">Tables & QR</h2><p className="mt-2 opacity-60 text-sm">Generating QR codes…</p></section>;
  if (!data) return null;

  return (
    <section>
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl">Tables & QR — {data.num_tables} tables</h2>
          <p className="text-xs" style={{ color: "var(--fg-muted)" }}>Print these stickers and place one on each table. Guests scan → land directly on your menu with their table pre-filled.</p>
        </div>
        <button onClick={printAll} data-testid="qr-print-all" className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>
          <Printer className="w-3.5 h-3.5" /> Print all
        </button>
      </div>
      <p className="text-[11px] mb-4" style={{ color: "var(--fg-muted)" }}>
        Need more or fewer tables? Adjust <strong>Number of tables</strong> in <em>Settings</em> and these regenerate automatically.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="qr-grid">
        {data.qr_codes.map((q) => (
          <div key={q.table} className="rounded-2xl p-4 flex flex-col items-center text-center" style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }} data-testid={`qr-card-${q.table}`}>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: "var(--accent)" }}>MnuVerse</div>
            <div className="font-display text-lg mt-0.5">{data.restaurant_name}</div>
            <img src={q.image_data_url} alt={q.label} className="w-32 h-32 mt-3 rounded-md bg-white p-1" />
            <div className="font-display text-2xl mt-3">{q.label}</div>
            <div className="text-[10px] font-mono mt-1 break-all" style={{ color: "var(--fg-dim)" }}>{q.path}</div>
            <button onClick={() => downloadOne(q)} data-testid={`qr-download-${q.table}`} className="mt-3 text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full hover:bg-white/5" style={{ border: "1px solid var(--border)" }}>
              <Download className="w-3 h-3" /> PNG
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- Sommelier prompt picker (controlled — state lives in parent) ---------- */

/* ---------- Prompt cache panel (warm/clear pre-computed answers) ---------- */
function PromptCachePanel({ resto }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/owner/assistant/prompt-cache"); setData(data); }
    catch { /* noop */ }
  };
  useEffect(() => { load(); }, [resto?.id]);

  const warm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/owner/assistant/prompt-cache/warm");
      toast.success(data.warmed > 0 ? `Pre-computed ${data.warmed} prompt${data.warmed === 1 ? "" : "s"}` : "Cache already fresh");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not warm cache"); }
    finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Clear all pre-computed prompt answers? Next guest tap will run a live LLM call again.")) return;
    setBusy(true);
    try { await api.delete("/owner/assistant/prompt-cache"); toast.success("Cache cleared"); load(); }
    catch { toast.error("Could not clear cache"); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const cachedCount = data.prompts.filter((p) => p.cached).length;
  const totalCount = data.prompts.length;

  return (
    <div className="p-4 rounded-2xl" style={{ background: "var(--bg-muted)", border: "1px solid var(--border)" }} data-testid="prompt-cache-panel">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="font-display text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4" style={{ color: "var(--accent)" }} />
            Pre-computed answers — {cachedCount}/{totalCount} ready
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--fg-muted)" }}>
            When a guest taps one of your prompt chips, we serve a cached answer in <strong>0 tokens</strong>. The cache auto-clears whenever you edit a dish.
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={warm} disabled={busy} data-testid="warm-cache-btn" className="gold-btn rounded-full px-4 py-1.5 text-xs font-semibold disabled:opacity-50">
            {busy ? "Warming…" : (cachedCount === totalCount && totalCount > 0 ? "Refresh" : "Warm cache")}
          </button>
          {data.total_cached_rows > 0 && (
            <button onClick={clear} disabled={busy} data-testid="clear-cache-btn" className="text-xs px-3 py-1.5 rounded-full hover:bg-white/5" style={{ border: "1px solid var(--border)", color: "var(--fg-muted)" }}>
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="mt-3 space-y-1.5" data-testid="cache-prompt-list">
        {data.prompts.map((p, i) => (
          <div key={`${p.prompt}-${i}`} className="flex items-center justify-between text-xs gap-2" data-testid={`cache-row-${i}`}>
            <span className="flex-1 truncate" style={{ color: "var(--fg-muted)" }}>"{p.prompt}"</span>
            {p.cached ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full text-emerald-400" style={{ background: "rgba(16,185,129,0.12)" }}>
                <Check className="w-3 h-3" /> ready · {p.recommended_count} dishes
              </span>
            ) : (
              <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "var(--glass-light)", color: "var(--fg-dim)" }}>not cached</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function SommelierPrompts({ selected, setSelected, defaults, max = 5 }) {
  const MAX = max;
  const [draft, setDraft] = useState("");

  const togglePreset = (p) => {
    setSelected((cur) => {
      if (cur.includes(p)) return cur.filter((x) => x !== p);
      if (cur.length >= MAX) { toast.error(`Pick up to ${MAX} prompts`); return cur; }
      return [...cur, p];
    });
  };
  const removeAt = (i) => setSelected((cur) => cur.filter((_, x) => x !== i));
  const addCustom = () => {
    const t = draft.trim();
    if (!t) return;
    if (selected.includes(t)) { toast.error("Already added"); return; }
    if (selected.length >= MAX) { toast.error(`Pick up to ${MAX} prompts`); return; }
    setSelected((cur) => [...cur, t]);
    setDraft("");
  };
  const restoreDefaults = () => setSelected(defaults.slice(0, MAX));

  return (
    <div className="p-4 rounded-2xl" style={{ background: "var(--bg-muted)", border: "1px solid var(--border)" }} data-testid="sommelier-prompts">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="font-display text-base">Conversation starters for your guests</div>
          <div className="text-xs" style={{ color: "var(--fg-muted)" }}>
            Pick up to {MAX} prompts that show as one-tap chips when guests open <em>Ask the menu</em>. These are saved <strong>per restaurant</strong> — switch restaurants from the header to configure each one independently. Returning guests still get auto-personalised chips on top of yours.
          </div>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: selected.length >= MAX ? "var(--accent)" : "var(--fg-muted)" }}>
          {selected.length}/{MAX} selected
        </span>
      </div>

      {/* Selected pills */}
      <div className="mt-3 flex flex-wrap gap-2 min-h-[40px]" data-testid="prompts-selected">
        {selected.length === 0 && <span className="text-xs italic" style={{ color: "var(--fg-dim)" }}>No prompts picked yet — guests will see the platform defaults.</span>}
        {selected.map((p, i) => (
          <span key={`s-${p}`} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full" style={{ background: "rgba(229,169,60,0.15)", color: "var(--accent)", border: "1px solid var(--accent)" }} data-testid={`prompt-selected-${i}`}>
            {p}
            <button onClick={() => removeAt(i)} className="opacity-70 hover:opacity-100" data-testid={`prompt-remove-${i}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>

      {/* Custom add */}
      <div className="mt-3 flex gap-2">
        <input
          value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
          placeholder='Add your own — e.g. "Recommend a fragrant biryani"'
          data-testid="prompt-custom-input"
          maxLength={120}
          className="flex-1 rounded-xl px-3 py-2 text-sm focus:outline-none"
          style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
        />
        <button onClick={addCustom} disabled={selected.length >= MAX || !draft.trim()} data-testid="prompt-custom-add" className="text-xs px-3 py-2 rounded-full font-semibold disabled:opacity-40" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>
          Add
        </button>
      </div>

      {/* Preset palette */}
      <div className="mt-4">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1.5" style={{ color: "var(--fg-muted)" }}>Pick from suggestions</div>
        <div className="flex flex-wrap gap-2" data-testid="prompts-palette">
          {defaults.map((p, i) => {
            const on = selected.includes(p);
            return (
              <button
                key={`d-${p}`}
                onClick={() => togglePreset(p)}
                data-testid={`prompt-preset-${i}`}
                className="text-xs px-3 py-1.5 rounded-full transition-colors"
                style={{
                  background: on ? "rgba(229,169,60,0.15)" : "transparent",
                  color: on ? "var(--accent)" : "var(--fg-muted)",
                  border: `1px solid ${on ? "var(--accent)" : "var(--border)"}`,
                }}
              >
                {on ? "✓ " : "+ "}{p}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4">
        <button onClick={restoreDefaults} type="button" data-testid="prompts-restore" className="text-xs px-4 py-2 rounded-full hover:bg-white/5" style={{ border: "1px solid var(--border)" }}>
          Use platform defaults
        </button>
      </div>
    </div>
  );
}

/* ---------- Settings tab ---------- */
function SettingsTab({ resto, onSaved }) {
  const [sub, setSub] = useState("general");
  const [form, setForm] = useState({
    name: resto.name || "",
    tagline: resto.tagline || "",
    cuisine: resto.cuisine || "",
    address: resto.address || "",
    logo_url: resto.logo_url || "",
    brand_color: resto.brand_color || "#E5A93C",
    num_tables: resto.num_tables ?? 14,
    payment_enabled: resto.payment_enabled !== false,
    reviews_enabled: resto.reviews_enabled !== false,
    public_reviews_enabled: resto.public_reviews_enabled !== false,
    show_order_count: resto.show_order_count !== false,
    require_login: resto.require_login !== false,
    assistant_enabled: resto.assistant_enabled !== false,
  });
  const [uploading, setUploading] = useState(false);
  // AI Sommelier is Elite-plan only. Keeping older Pro/Ultra in the check
  // protects any legacy tenants whose tier rename pipeline didn't run.
  const isPro = ["elite", "ultra"].includes((resto.subscription_tier || "").toLowerCase());

  // Sommelier prompts — loaded per-restaurant so switching restaurants repopulates
  const [promptSelected, setPromptSelected] = useState([]);
  const [promptDefaults, setPromptDefaults] = useState([]);
  const [promptsLoaded, setPromptsLoaded] = useState(false);
  const [savingSommelier, setSavingSommelier] = useState(false);

  useEffect(() => {
    if (!isPro) { setPromptsLoaded(true); return; }
    let cancelled = false;
    setPromptsLoaded(false);
    (async () => {
      try {
        const { data } = await api.get("/owner/assistant/prompts");
        if (cancelled) return;
        setPromptSelected(data.selected || []);
        setPromptDefaults(data.platform_defaults || []);
      } catch { /* noop */ }
      finally { if (!cancelled) setPromptsLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [isPro, resto.id]);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: `${BACKEND}${data.url}` }));
      toast.success("Logo uploaded");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const save = async () => {
    try { await api.put("/owner/restaurant", form); toast.success("Settings saved"); onSaved(); }
    catch { toast.error("Save failed"); }
  };

  const saveSommelier = async () => {
    setSavingSommelier(true);
    try {
      // Save the toggle via restaurant, then the per-tenant prompts.
      await api.put("/owner/restaurant", { assistant_enabled: form.assistant_enabled });
      if (isPro) {
        await api.put("/owner/assistant/prompts", { prompts: promptSelected });
      }
      toast.success("AI Sommelier settings saved for this restaurant");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save sommelier settings");
    } finally {
      setSavingSommelier(false);
    }
  };

  return (
    <section className="max-w-2xl">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-display text-2xl">Restaurant Settings</h2>
        <div className="inline-flex rounded-full p-1" style={{ background: "var(--bg-muted)", border: "1px solid var(--border)" }} data-testid="settings-subnav">
          {[
            { key: "general", label: "General" },
            { key: "sommelier", label: "AI Sommelier" },
          ].map((s) => {
            const on = sub === s.key;
            return (
              <button
                key={s.key}
                onClick={() => setSub(s.key)}
                data-testid={`settings-sub-${s.key}`}
                className="text-xs px-4 py-1.5 rounded-full font-semibold transition-colors"
                style={{
                  background: on ? "var(--accent)" : "transparent",
                  color: on ? "var(--accent-fg)" : "var(--fg-muted)",
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {sub === "general" && (
      <div className="space-y-4 p-5 rounded-2xl" style={{ border: "1px solid var(--border)", background: "var(--bg-card)" }} data-testid="settings-form">
        <div className="flex gap-4 items-center">
          {form.logo_url ? (
            <img src={form.logo_url} alt="logo" className="w-20 h-20 rounded-xl object-cover" data-testid="settings-logo-preview" />
          ) : (
            <div className="w-20 h-20 rounded-xl flex items-center justify-center" style={{ background: "var(--glass-light)" }}><ImagePlus className="w-6 h-6" style={{ color: "var(--fg-dim)" }} /></div>
          )}
          <div>
            <div className="text-xs" style={{ color: "var(--fg-muted)" }}>Restaurant logo</div>
            <label className="mt-1 inline-block text-xs gold-btn rounded-full px-3 py-1.5 font-semibold cursor-pointer">
              {uploading ? "Uploading…" : "Upload"}
              <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="settings-upload-logo" />
            </label>
          </div>
        </div>
        <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="settings-name" />
        <Input label="Tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} testid="settings-tagline" />
        <div className="grid sm:grid-cols-2 gap-3">
          <CuisineTagInput label="Cuisines" value={form.cuisine} onChange={(v) => setForm({ ...form, cuisine: v })} testid="settings-cuisine" />
          <Input label="Brand color" type="color" value={form.brand_color} onChange={(v) => setForm({ ...form, brand_color: v })} testid="settings-brand-color" />
        </div>
        <Input label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="settings-address" />
        <Input label="Number of tables" type="number" value={form.num_tables} onChange={(v) => setForm({ ...form, num_tables: Math.max(1, Number(v) || 1) })} testid="settings-num-tables" />
        <label className="flex items-center gap-3 p-3 rounded-xl cursor-pointer" style={{ background: "var(--bg-muted)", border: "1px solid var(--border)" }}>
          <input type="checkbox" checked={form.payment_enabled} onChange={(e) => setForm({ ...form, payment_enabled: e.target.checked })} data-testid="settings-payment-enabled" />
          <div>
            <div className="font-display text-base">Accept in-app payments</div>
            <div className="text-xs" style={{ color: "var(--fg-muted)" }}>When off, guests pay the waiter directly — you'll mark the order paid from the Staff console.</div>
          </div>
        </label>
        <label className="flex items-start gap-3 p-3 rounded-xl cursor-pointer" style={{ background: "var(--bg-muted)", border: "1px solid var(--border)" }}>
          <input type="checkbox" checked={form.reviews_enabled} onChange={(e) => setForm({ ...form, reviews_enabled: e.target.checked })} data-testid="settings-reviews-enabled" />
          <div>
            <div className="font-display text-base">Show dish reviews after payment</div>
            <div className="text-xs" style={{ color: "var(--fg-muted)" }}>When on, guests see a 1-5 star rating prompt for each dish on the order success screen once their meal is served. Turn off to skip the post-meal rating ask.</div>
          </div>
        </label>
        <label className="flex items-start gap-3 p-3 rounded-xl cursor-pointer" style={{ background: "var(--bg-muted)", border: "1px solid var(--border)" }}>
          <input type="checkbox" checked={form.show_order_count} onChange={(e) => setForm({ ...form, show_order_count: e.target.checked })} data-testid="settings-show-order-count" />
          <div>
            <div className="font-display text-base">Show "ordered N×" social proof on dishes</div>
            <div className="text-xs" style={{ color: "var(--fg-muted)" }}>When on, every dish card in the menu shows how many times it has been ordered overall (all-time count). Turn off to hide the number from guests — your internal analytics are unaffected.</div>
          </div>
        </label>
        <label className="flex items-start gap-3 p-3 rounded-xl cursor-pointer" style={{ background: "var(--bg-muted)", border: "1px solid var(--border)" }}>
          <input type="checkbox" checked={form.public_reviews_enabled} onChange={(e) => setForm({ ...form, public_reviews_enabled: e.target.checked })} data-testid="settings-public-reviews-enabled" />
          <div>
            <div className="font-display text-base">Show customer reviews on menu</div>
            <div className="text-xs" style={{ color: "var(--fg-muted)" }}>When on, guests browsing your menu see the star rating and review count on each dish. Turn off to hide all customer reviews from the public menu — ratings are still collected privately for your Quality Control dashboard.</div>
          </div>
        </label>
        <label className="flex items-start gap-3 p-3 rounded-xl cursor-pointer" style={{ background: "var(--bg-muted)", border: "1px solid var(--border)" }}>
          <input type="checkbox" checked={form.require_login} onChange={(e) => setForm({ ...form, require_login: e.target.checked })} data-testid="settings-require-login" />
          <div>
            <div className="font-display text-base">Require sign-in before placing orders</div>
            <div className="text-xs" style={{ color: "var(--fg-muted)" }}>
              {form.require_login
                ? "ON — guests must verify their phone with OTP before they can send an order. Best for loyalty perks, order history and reorders."
                : "OFF — guests can place an order with just a name and table number. Faster for walk-ins; loyalty perks are skipped for guest orders."}
            </div>
          </div>
        </label>
        <button onClick={save} data-testid="settings-save" className="gold-btn rounded-full px-6 py-2.5 font-semibold mt-2">Save settings</button>
      </div>
      )}

      {sub === "sommelier" && (
      <div className="space-y-4 p-5 rounded-2xl" style={{ border: "1px solid var(--border)", background: "var(--bg-card)" }} data-testid="settings-sommelier">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: "var(--fg-muted)" }}>
          Configuring for: <span style={{ color: "var(--accent)" }}>{resto.name}</span>
        </div>
        <label className={`flex items-start gap-3 p-3 rounded-xl ${isPro ? "cursor-pointer" : "opacity-70"}`} style={{ background: "var(--bg-muted)", border: "1px solid var(--border)" }}>
          <input type="checkbox" checked={isPro && form.assistant_enabled} disabled={!isPro} onChange={(e) => setForm({ ...form, assistant_enabled: e.target.checked })} data-testid="settings-assistant-enabled" />
          <div>
            <div className="font-display text-base flex items-center gap-2">
              AI Sommelier
              <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full" style={{ background: "rgba(229,169,60,0.15)", color: "var(--accent)", border: "1px solid var(--accent)" }}>PRO</span>
            </div>
            <div className="text-xs" style={{ color: "var(--fg-muted)" }}>
              {isPro
                ? "When on, guests see the floating \"Ask the menu\" button and can chat (voice + text) with an AI sommelier. Turn off any time."
                : "Available on the Pro tier. Contact platform admin to upgrade your plan."}
            </div>
          </div>
        </label>

        {isPro && promptsLoaded && (
          <SommelierPrompts
            selected={promptSelected}
            setSelected={setPromptSelected}
            defaults={promptDefaults}
            max={5}
          />
        )}

        {isPro && (
          <PromptCachePanel resto={resto} />
        )}

        <button onClick={saveSommelier} disabled={savingSommelier} data-testid="settings-save-sommelier" className="gold-btn rounded-full px-6 py-2.5 font-semibold mt-2 disabled:opacity-50">
          {savingSommelier ? "Saving…" : "Save AI Sommelier settings"}
        </button>
      </div>
      )}
    </section>
  );
}

/* ---------- Item editor (unchanged from v3, but cost visibility is kept since owner needs it) ---------- */
function ItemEditor({ item, categories, onClose, onSave, onDelete }) {
  const [form, setForm] = useState({
    name: item?.name || "", description: item?.description || "",
    price: item?.price ?? 0, cost: item?.cost ?? 0,
    category_id: item?.category_id || categories[0]?.id || "",
    image_url: item?.image_url || "", video_url: item?.video_url || "",
    images: item?.images || (item?.image_url ? [item.image_url] : []),
    videos: item?.videos || (item?.video_url ? [item.video_url] : []),
    is_featured: item?.is_featured ?? false, is_sold_out: item?.is_sold_out ?? false,
    is_veg: item?.is_veg ?? false, is_spicy: item?.is_spicy ?? false, is_chef_special: item?.is_chef_special ?? false,
    allergens: item?.allergens || [], tags: item?.tags || [], ingredients: item?.ingredients || [],
    plating_video_url: item?.plating_video_url || "",
    culinary_definition: item?.culinary_definition || "",
    calories: item?.calories ?? "",
  });
  const [uploading, setUploading] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState("");
  const [showCost, setShowCost] = useState(false);
  const [newIngredient, setNewIngredient] = useState("");
  const [aiBusy, setAiBusy] = useState(""); // "definition" | "allergens" | ""

  const addIngredient = () => {
    const t = newIngredient.trim();
    if (!t) return;
    if ((form.ingredients || []).some((x) => x.toLowerCase() === t.toLowerCase())) { setNewIngredient(""); return; }
    setForm((f) => ({ ...f, ingredients: [...(f.ingredients || []), t] }));
    setNewIngredient("");
  };
  const removeIngredient = (val) => setForm((f) => ({ ...f, ingredients: (f.ingredients || []).filter((x) => x !== val) }));

  const handleUpload = async (e, kind) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const url = `${BACKEND}${data.url}`;
        setForm((f) => kind === "image" ? { ...f, images: [...f.images, url], image_url: f.image_url || url } : { ...f, videos: [...f.videos, url], video_url: f.video_url || url });
      }
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };
  const addImageByUrl = () => { if (!newImageUrl.trim()) return; setForm((f) => ({ ...f, images: [...f.images, newImageUrl.trim()], image_url: f.image_url || newImageUrl.trim() })); setNewImageUrl(""); };
  const removeImage = (u) => setForm((f) => ({ ...f, images: f.images.filter((x) => x !== u), image_url: f.image_url === u ? (f.images.find((x) => x !== u) || "") : f.image_url }));
  const setPrimary = (u) => setForm((f) => ({ ...f, image_url: u }));
  const removeVideo = (u) => setForm((f) => ({ ...f, videos: f.videos.filter((x) => x !== u), video_url: f.video_url === u ? (f.videos.find((x) => x !== u) || "") : f.video_url }));
  const toggleAllergen = (a) => setForm((f) => ({ ...f, allergens: f.allergens.includes(a) ? f.allergens.filter((x) => x !== a) : [...f.allergens, a] }));

  const generateDefinition = async () => {
    if (!item?.id) { toast.error("Save the dish first, then generate a definition."); return; }
    setAiBusy("definition");
    try {
      const { data } = await api.post(`/owner/menu/${item.id}/ai/generate-definition`);
      if (data?.definition) {
        setForm((f) => ({ ...f, culinary_definition: data.definition }));
        toast.success("Drafted with AI — review and Save");
      } else {
        toast.error("AI returned an empty draft");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI unavailable — try again");
    } finally { setAiBusy(""); }
  };

  const detectAllergens = async () => {
    if (!item?.id) { toast.error("Save the dish first, then auto-detect allergens."); return; }
    setAiBusy("allergens");
    try {
      const { data } = await api.post(`/owner/menu/${item.id}/ai/detect-allergens`);
      const detected = data?.allergens || [];
      // Merge detected into existing — owner can deselect anything they disagree with.
      setForm((f) => {
        const next = [...f.allergens];
        for (const a of detected) if (!next.includes(a)) next.push(a);
        return { ...f, allergens: next };
      });
      toast.success(detected.length ? `AI flagged ${detected.length} allergen${detected.length === 1 ? "" : "s"}` : "AI didn't flag any allergens");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI unavailable — try again");
    } finally { setAiBusy(""); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl rounded-3xl p-6 max-h-[92vh] overflow-y-auto" style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }} data-testid="item-editor">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl">{item ? "Edit dish" : "Add dish"}</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "var(--glass-light)" }} data-testid="close-editor"><X className="w-4 h-4" /></button>
        </div>
        <div className="mt-5 space-y-3">
          <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="editor-name" />
          <Area label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="editor-desc" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Price (₹)" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: parseFloat(v) || 0 })} testid="editor-price" />
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs" style={{ color: "var(--fg-muted)" }}>Cost (₹)</label>
                <button type="button" onClick={() => setShowCost((v) => !v)} className="text-[10px] opacity-60 hover:opacity-100" data-testid="toggle-cost">{showCost ? "hide" : "show"}</button>
              </div>
              {showCost ? (
                <input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: parseFloat(e.target.value) || 0 })} data-testid="editor-cost" className="w-full rounded-xl px-3 py-2.5 focus:outline-none" style={{ border: "1px solid var(--border)", background: "transparent" }} />
              ) : (
                <div className="w-full rounded-xl px-3 py-2.5 font-mono text-sm" style={{ border: "1px dashed var(--border)", color: "var(--fg-dim)" }}>hidden · for margin engine</div>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs flex items-center gap-1.5" style={{ color: "var(--fg-muted)" }}>
              Calories <span className="text-[10px]" style={{ color: "var(--fg-dim)" }}>— optional · shown to guests as e.g. "320 kcal"</span>
            </label>
            <input
              type="number"
              min="0"
              value={form.calories}
              onChange={(e) => setForm({ ...form, calories: e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0) })}
              placeholder="e.g. 320"
              data-testid="editor-calories"
              className="w-full rounded-xl px-3 py-2.5 focus:outline-none transition bg-transparent text-sm"
              style={{ border: "1px solid var(--border)" }}
            />
          </div>
          <div>
            <div className="text-xs mb-1" style={{ color: "var(--fg-muted)" }}>Category</div>
            <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="w-full rounded-xl px-3 py-2.5" style={{ background: "var(--bg-muted)", border: "1px solid var(--border)" }} data-testid="editor-cat">
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <div className="text-xs mb-1 flex items-center gap-2" style={{ color: "var(--fg-muted)" }}><ImagePlus className="w-3.5 h-3.5" /> Images ({form.images.length})</div>
            <div className="grid grid-cols-4 gap-2">
              {form.images.map((u, idx) => (
                <div key={idx} className="relative rounded-xl overflow-hidden" style={{ border: `1px solid ${form.image_url === u ? "var(--accent)" : "var(--border)"}` }} data-testid={`editor-img-${idx}`}>
                  <img src={u} alt="" className="w-full aspect-square object-cover" />
                  <button onClick={() => setPrimary(u)} className="absolute top-1 left-1 text-[9px] px-1.5 py-0.5 rounded-full bg-black/70 text-white hover:bg-[#E5A93C] hover:text-black" data-testid={`editor-img-primary-${idx}`}>
                    {form.image_url === u ? "★ Cover" : "Set cover"}
                  </button>
                  <button onClick={() => removeImage(u)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-500" data-testid={`editor-img-del-${idx}`}><X className="w-3 h-3" /></button>
                </div>
              ))}
              <label className="aspect-square rounded-xl flex flex-col items-center justify-center cursor-pointer text-xs" style={{ border: "1px dashed var(--border)", color: "var(--fg-muted)" }}>
                <ImagePlus className="w-5 h-5 mb-1" /> Upload
                <input type="file" accept="image/*" multiple onChange={(e) => handleUpload(e, "image")} className="hidden" data-testid="editor-upload-image" />
              </label>
            </div>
            <div className="mt-2 flex gap-2">
              <input value={newImageUrl} onChange={(e) => setNewImageUrl(e.target.value)} placeholder="Paste image URL" className="flex-1 bg-transparent rounded-lg px-3 py-2 text-sm" style={{ border: "1px solid var(--border)" }} data-testid="editor-img-url" />
              <button type="button" onClick={addImageByUrl} className="text-xs px-3 py-2 rounded-lg hover:bg-white/5" style={{ border: "1px solid var(--border)" }} data-testid="editor-img-url-add">Add URL</button>
            </div>
          </div>
          <div>
            <div className="text-xs mb-1 flex items-center gap-2" style={{ color: "var(--fg-muted)" }}><Video className="w-3.5 h-3.5" /> Videos ({form.videos.length})</div>
            <div className="grid grid-cols-3 gap-2">
              {form.videos.map((u, idx) => (
                <div key={idx} className="relative rounded-xl overflow-hidden aspect-square bg-black" data-testid={`editor-vid-${idx}`} style={{ border: "1px solid var(--border)" }}>
                  <video src={u} className="w-full h-full object-cover" muted />
                  <button onClick={() => removeVideo(u)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 flex items-center justify-center hover:bg-red-500"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <label className="aspect-square rounded-xl flex flex-col items-center justify-center cursor-pointer text-xs" style={{ border: "1px dashed var(--border)", color: "var(--fg-muted)" }}>
                <Video className="w-5 h-5 mb-1" /> Upload
                <input type="file" accept="video/*" multiple onChange={(e) => handleUpload(e, "video")} className="hidden" data-testid="editor-upload-video" />
              </label>
            </div>
          </div>
          {uploading && <div className="text-xs" style={{ color: "var(--accent)" }}>Uploading…</div>}
          <div>
            <div className="text-xs mb-1 flex items-center gap-2" style={{ color: "var(--fg-muted)" }}>
              <Sparkles className="w-3.5 h-3.5" /> Ingredients
              <span className="text-[10px]" style={{ color: "var(--fg-dim)" }}>— helps the AI Sommelier answer "what's in this?" and recommend on flavour</span>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="editor-ingredients-list">
              {(form.ingredients || []).length === 0 && <span className="text-[11px] italic" style={{ color: "var(--fg-dim)" }}>None added yet.</span>}
              {(form.ingredients || []).map((ing, i) => (
                <span key={ing} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full" style={{ background: "rgba(229,169,60,0.12)", color: "var(--accent)", border: "1px solid var(--accent)" }} data-testid={`editor-ing-${i}`}>
                  {ing}
                  <button type="button" onClick={() => removeIngredient(ing)} className="opacity-70 hover:opacity-100" data-testid={`editor-ing-del-${i}`}><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={newIngredient}
                onChange={(e) => setNewIngredient(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addIngredient(); } }}
                placeholder="e.g. wild mushroom, truffle oil, saffron"
                data-testid="editor-ing-input"
                className="flex-1 rounded-lg px-3 py-2 text-sm bg-transparent"
                style={{ border: "1px solid var(--border)" }}
              />
              <button type="button" onClick={addIngredient} data-testid="editor-ing-add" className="text-xs px-3 py-2 rounded-lg font-semibold" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>Add</button>
            </div>
          </div>
          <div>
            <div className="text-xs mb-1 flex items-center justify-between" style={{ color: "var(--fg-muted)" }}>
              <span className="flex items-center gap-2"><ShieldAlert className="w-3.5 h-3.5" /> Allergens</span>
              <button
                type="button"
                onClick={detectAllergens}
                disabled={aiBusy === "allergens"}
                data-testid="editor-detect-allergens"
                className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-full disabled:opacity-50"
                style={{ border: "1px solid var(--accent)", color: "var(--accent)" }}
                title="AI scans ingredients + description and ticks the likely allergens — review before saving"
              >
                <Wand2 className="w-3 h-3" /> {aiBusy === "allergens" ? "Detecting…" : "Detect with AI"}
              </button>
            </div>
            <div className="flex gap-1 flex-wrap">
              {["Gluten", "Dairy", "Nuts", "Peanuts", "Shellfish", "Eggs", "Fish", "Soy", "Sesame"].map((a) => (
                <button key={a} type="button" onClick={() => toggleAllergen(a)} data-testid={`editor-al-${a}`} style={{ background: form.allergens.includes(a) ? "var(--accent)" : "transparent", color: form.allergens.includes(a) ? "var(--accent-fg)" : "var(--fg)", border: `1px solid ${form.allergens.includes(a) ? "var(--accent)" : "var(--border)"}` }} className="text-xs px-3 py-1 rounded-full">{a}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs mb-1 flex items-center justify-between" style={{ color: "var(--fg-muted)" }}>
              <span className="flex items-center gap-2"><ChefHat className="w-3.5 h-3.5" /> Culinary definition <span className="text-[10px]" style={{ color: "var(--fg-dim)" }}>— shown to waitstaff in the Assistant tab</span></span>
              <button
                type="button"
                onClick={generateDefinition}
                disabled={aiBusy === "definition"}
                data-testid="editor-generate-definition"
                className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-full disabled:opacity-50"
                style={{ border: "1px solid var(--accent)", color: "var(--accent)" }}
              >
                <Wand2 className="w-3 h-3" /> {aiBusy === "definition" ? "Drafting…" : "Generate with AI"}
              </button>
            </div>
            <textarea
              rows={3}
              value={form.culinary_definition}
              onChange={(e) => setForm({ ...form, culinary_definition: e.target.value })}
              placeholder="A 2-3 sentence brief your servers can recite to a curious guest. Click 'Generate with AI' to draft, then edit."
              data-testid="editor-culinary-definition"
              className="w-full rounded-xl px-3 py-2.5 focus:outline-none transition resize-none bg-transparent text-sm"
              style={{ border: "1px solid var(--border)" }}
            />
          </div>
          <div>
            <div className="text-xs mb-1 flex items-center gap-2" style={{ color: "var(--fg-muted)" }}>
              <Video className="w-3.5 h-3.5" /> Plating guide video URL
              <span className="text-[10px]" style={{ color: "var(--fg-dim)" }}>— optional. Staff can replay it in the Assistant tab.</span>
            </div>
            <input
              value={form.plating_video_url}
              onChange={(e) => setForm({ ...form, plating_video_url: e.target.value })}
              placeholder="Paste a YouTube/Vimeo/MP4 URL"
              data-testid="editor-plating-video-url"
              className="w-full rounded-xl px-3 py-2.5 focus:outline-none transition bg-transparent text-sm"
              style={{ border: "1px solid var(--border)" }}
            />
          </div>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <ToggleRow label="Vegetarian" icon={<Leaf className="w-3.5 h-3.5" />} value={form.is_veg} onChange={(v) => setForm({ ...form, is_veg: v })} testid="editor-veg" />
            <ToggleRow label="Spicy" icon={<Spicy className="w-3.5 h-3.5" />} value={form.is_spicy} onChange={(v) => setForm({ ...form, is_spicy: v })} testid="editor-spicy" />
            <ToggleRow label="Chef's Special" icon={<Award className="w-3.5 h-3.5" />} value={form.is_chef_special} onChange={(v) => setForm({ ...form, is_chef_special: v })} testid="editor-chef" />
            <ToggleRow label="Featured" icon={<Star className="w-3.5 h-3.5" />} value={form.is_featured} onChange={(v) => setForm({ ...form, is_featured: v })} testid="editor-featured" />
            <ToggleRow label="Sold out" icon={<X className="w-3.5 h-3.5" />} value={form.is_sold_out} onChange={(v) => setForm({ ...form, is_sold_out: v })} testid="editor-sold-out" />
          </div>
        </div>
        <div className="mt-6 flex justify-between items-center">
          {item && <button onClick={() => onDelete(item.id)} className="text-xs text-red-400 hover:underline" data-testid="delete-item">Delete dish</button>}
          <button onClick={() => {
            const payload = { ...form };
            payload.calories = form.calories === "" || form.calories == null
              ? null
              : Math.max(0, parseInt(form.calories, 10) || 0);
            onSave(payload, item?.id);
          }} className="gold-btn rounded-full px-5 py-2.5 font-semibold ml-auto" data-testid="save-item">Save</button>
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ label, icon, value, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!value)} data-testid={testid} style={{ background: value ? "var(--accent)" : "transparent", color: value ? "var(--accent-fg)" : "var(--fg)", border: `1px solid ${value ? "var(--accent)" : "var(--border)"}` }} className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm">
      <span className="w-4 h-4 rounded flex items-center justify-center" style={{ border: `1px solid ${value ? "currentColor" : "var(--border-strong)"}` }}>
        {value && <span className="w-2 h-2 rounded-sm" style={{ background: "currentColor" }} />}
      </span>
      {icon} {label}
    </button>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs" style={{ color: "var(--fg-muted)" }}>{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full rounded-xl px-3 py-2.5 focus:outline-none transition bg-transparent" style={{ border: "1px solid var(--border)" }} />
    </div>
  );
}
function Area({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs" style={{ color: "var(--fg-muted)" }}>{label}</label>
      <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full rounded-xl px-3 py-2.5 focus:outline-none transition resize-none bg-transparent" style={{ border: "1px solid var(--border)" }} />
    </div>
  );
}

// --- Trial / Plan UI ---

const PLAN_PRICES = { Starter: 6500, Pro: 12000, Elite: 18000 };
const PLAN_BENEFITS = {
  Starter: ["Unlimited menu items & photos", "Menu management", "Unlimited QR generation", "Free loyalty program", "Customer CRM"],
  Pro: ["Everything in Starter", "KOT — kitchen display", "Billing management", "Payment gateway", "POS integrations"],
  Elite: ["Everything in Pro", "AI Sommelier (voice + text)", "AI demand predictions", "Advanced analytics", "Exact AI token tracking"],
};

function TrialBanner({ billing, onUpgrade }) {
  const days = billing.trial_days_left;
  const plan = billing.plan || "Starter";
  if (!billing.trial_active && !billing.trial_expired) {
    return (
      <div className="mb-4 p-3 rounded-2xl flex items-center justify-between flex-wrap gap-3" style={{ background: "var(--bg-muted)", border: "1px solid var(--border)" }} data-testid="plan-strip">
        <div className="text-xs flex items-center gap-2">
          <span className="px-2 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>{plan === "Elite" ? "Elite AI" : plan} plan</span>
          <span style={{ color: "var(--fg-muted)" }}>Active · ₹{(PLAN_PRICES[plan] || 0).toLocaleString("en-IN")}/yr</span>
        </div>
        {plan !== "Elite" && (
          <button onClick={onUpgrade} data-testid="open-upgrade-modal" className="text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5" style={{ border: "1px solid var(--accent)", color: "var(--accent)" }}>
            <Sparkles className="w-3 h-3" /> Upgrade plan
          </button>
        )}
      </div>
    );
  }
  const expired = billing.trial_expired || (days != null && days <= 0);
  const urgent = !expired && days != null && days <= 7;
  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-4 p-4 rounded-2xl flex items-center justify-between flex-wrap gap-3"
      style={{
        background: expired ? "rgba(239,68,68,0.10)" : (urgent ? "rgba(229,169,60,0.12)" : "rgba(229,169,60,0.08)"),
        border: `1px solid ${expired ? "rgba(239,68,68,0.4)" : "rgba(229,169,60,0.4)"}`,
      }}
      data-testid="trial-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: expired ? "rgba(239,68,68,0.2)" : "rgba(229,169,60,0.2)" }}>
          <Sparkles className="w-5 h-5" style={{ color: expired ? "#ef4444" : "#E5A93C" }} />
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: "var(--fg-muted)" }}>
            {expired ? "Trial ended" : `Trial · ${plan === "Elite" ? "Elite AI" : plan} plan`}
          </div>
          <div className="font-display text-lg mt-0.5" data-testid="trial-days-left">
            {expired
              ? "Your free trial has ended"
              : (days === 1 ? "1 day left in your free trial" : `${days} days left in your free trial`)}
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--fg-muted)" }}>
            {expired
              ? "Upgrade now to keep your dashboard, menu and customer data live."
              : `After ${days} more day${days === 1 ? "" : "s"}, you'll need to pick a plan to keep your dashboard active. Cancel any time before then.`}
          </div>
        </div>
      </div>
      <button onClick={onUpgrade} data-testid="open-upgrade-modal" className="gold-btn rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 shrink-0">
        {expired ? "Reactivate now" : "Upgrade & lock in"}
      </button>
    </motion.div>
  );
}

function UpgradeModal({ billing, onClose, onUpgraded }) {
  const [picking, setPicking] = useState(false);
  const currentPlan = billing.plan;
  const upgradeTo = async (plan) => {
    if (plan === currentPlan) { toast.info(`You're already on ${plan}`); return; }
    setPicking(true);
    try {
      await api.post("/owner/upgrade-plan", { plan });
      toast.success(`Switched to ${plan === "Elite" ? "Elite AI" : plan} — welcome aboard`);
      onUpgraded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not switch plan");
    } finally { setPicking(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="upgrade-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl rounded-3xl p-6 max-h-[92vh] overflow-y-auto" style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: "var(--fg-muted)" }}>Choose a plan</div>
            <div className="font-display text-2xl mt-1">Pick the plan that fits where you're heading</div>
            <div className="text-xs mt-1" style={{ color: "var(--fg-muted)" }}>You can upgrade or downgrade any time.</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10" data-testid="close-upgrade-modal"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          {["Starter", "Pro", "Elite"].map((p) => {
            const isCurrent = p === currentPlan;
            const isRecommended = p === "Pro";
            return (
              <div
                key={p}
                data-testid={`upgrade-plan-${p}`}
                className="rounded-2xl p-4 flex flex-col"
                style={{
                  background: isCurrent ? "var(--bg-muted)" : "transparent",
                  border: `1px solid ${isCurrent ? "var(--accent)" : "var(--border)"}`,
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="font-display text-lg">{p === "Elite" ? "Elite AI" : p}</div>
                  {isCurrent && <span className="text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>Current</span>}
                  {!isCurrent && isRecommended && <span className="text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "rgba(229,169,60,0.18)", color: "var(--accent)", border: "1px solid rgba(229,169,60,0.4)" }}>Popular</span>}
                </div>
                <div className="mt-2 font-display text-3xl" data-testid={`upgrade-price-${p}`}>₹{PLAN_PRICES[p].toLocaleString("en-IN")}<span className="text-sm" style={{ color: "var(--fg-muted)" }}>/yr</span></div>
                <ul className="mt-3 space-y-1.5 flex-1">
                  {PLAN_BENEFITS[p].map((b) => (
                    <li key={b} className="text-xs flex items-start gap-1.5">
                      <Check className="w-3 h-3 mt-0.5 shrink-0" style={{ color: "var(--accent)" }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => upgradeTo(p)}
                  disabled={picking || isCurrent}
                  data-testid={`pick-plan-${p}`}
                  className={`mt-4 rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-50 ${isCurrent ? "" : "gold-btn"}`}
                  style={isCurrent ? { border: "1px solid var(--border)", color: "var(--fg-muted)" } : undefined}
                >
                  {isCurrent ? "On this plan" : `Switch to ${p === "Elite" ? "Elite AI" : p}`}
                </button>
              </div>
            );
          })}
        </div>
        <div className="text-[11px] mt-4 text-center" style={{ color: "var(--fg-muted)" }}>
          Payment gateway integration coming soon — switching plans is recorded immediately and our team will reach out for billing.
        </div>
      </div>
    </div>
  );
}

